import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCalendarEvent, IconEye, IconReportMoney } from '@tabler/icons-react';
import { getAccentColor } from '../../lib/jlfcTheme';
import JlfcPaginatedTable from '../../components/JlfcPaginatedTable';
import TableActionIcon, { TableActionsGroup } from '../../components/TableActionIcon';
import ClientPortalDetailModal from '../../components/client/ClientPortalDetailModal';
import ClientPortalStatsPanel from '../../components/client/ClientPortalStatsPanel';
import ClientPortalPlanningPanel from '../../components/client/ClientPortalPlanningPanel';
import { authFetch, parseJsonResponse } from '../../lib/api';

function formatMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
}

function formatDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR');
}

function formatNextRdvLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Prochain RDV';
  return d.toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function sumField(rows, key) {
  return rows.reduce((acc, row) => {
    const n = Number(row[key]);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function TableTotalsBar({ children }) {
  return (
    <Group gap="md" wrap="wrap" px="md" py="sm">
      <ThemeIcon size="lg" radius="md" color={getAccentColor()} variant="light">
        <IconReportMoney size={20} />
      </ThemeIcon>
      <Group gap="xl" wrap="wrap">
        {children}
      </Group>
    </Group>
  );
}

function ControlesTableSummary({ rows }) {
  const relances = rows.filter((r) => r.relance).length;
  return (
    <TableTotalsBar>
      <Text size="sm">
        Total :{' '}
        <Text span fw={700} c={`${getAccentColor()}.8`}>
          {rows.length} contrôle{rows.length > 1 ? 's' : ''}
        </Text>
      </Text>
      {relances > 0 ? (
        <Text size="sm">
          Relances :{' '}
          <Text span fw={700} c="orange.8">
            {relances}
          </Text>
        </Text>
      ) : null}
    </TableTotalsBar>
  );
}

function FacturesTableSummary({ rows }) {
  return (
    <TableTotalsBar>
      <Text size="sm">
        Montant TTC :{' '}
        <Text span fw={700} c={`${getAccentColor()}.8`}>
          {formatMoney(sumField(rows, 'montant_du_ttc'))}
        </Text>
      </Text>
      <Text size="sm">
        Restant dû :{' '}
        <Text span fw={700} c={`${getAccentColor()}.8`}>
          {formatMoney(sumField(rows, 'restant_du'))}
        </Text>
      </Text>
    </TableTotalsBar>
  );
}

function EnginsTableSummary({ rows }) {
  const electriques = rows.filter((r) => r.electrique).length;
  return (
    <TableTotalsBar>
      <Text size="sm">
        Total :{' '}
        <Text span fw={700} c={`${getAccentColor()}.8`}>
          {rows.length} engin{rows.length > 1 ? 's' : ''}
        </Text>
      </Text>
      {electriques > 0 ? (
        <Text size="sm">
          Électriques :{' '}
          <Text span fw={700} c="yellow.8">
            {electriques}
          </Text>
        </Text>
      ) : null}
    </TableTotalsBar>
  );
}

function ReglementsTableSummary({ rows }) {
  return (
    <TableTotalsBar>
      <Text size="sm">
        Total :{' '}
        <Text span fw={700} c={`${getAccentColor()}.8`}>
          {rows.length} règlement{rows.length > 1 ? 's' : ''}
        </Text>
      </Text>
      <Text size="sm">
        Montant :{' '}
        <Text span fw={700} c={`${getAccentColor()}.8`}>
          {formatMoney(sumField(rows, 'montant'))}
        </Text>
      </Text>
    </TableTotalsBar>
  );
}

const CONTROLE_COLUMNS = [
  {
    key: 'date_controle',
    label: 'Date contrôle',
    render: (r) => formatDate(r.date_controle),
    sortValue: (r) => r.date_controle || '',
  },
  {
    key: 'engin_label',
    label: 'Engin',
    render: (r) => r.engin_label || '—',
  },
  {
    key: 'date_fin_validite',
    label: 'Fin validité',
    render: (r) => formatDate(r.date_fin_validite),
    sortValue: (r) => r.date_fin_validite || '',
  },
  {
    key: 'num_facture',
    label: 'Facture',
    render: (r) => r.num_facture || '—',
  },
  {
    key: 'relance',
    label: 'Relance',
    render: (r) =>
      r.relance ? (
        <Badge color="orange" variant="light" size="sm">
          Oui
        </Badge>
      ) : (
        '—'
      ),
  },
];

const FACTURE_COLUMNS = [
  {
    key: 'num_facture',
    label: 'N° facture',
    render: (r) => r.num_facture || '—',
  },
  {
    key: 'date_emission',
    label: 'Date',
    render: (r) => formatDate(r.date_emission),
    sortValue: (r) => r.date_emission || '',
  },
  {
    key: 'montant_du_ttc',
    label: 'Montant TTC',
    render: (r) => formatMoney(r.montant_du_ttc),
    sortValue: (r) => Number(r.montant_du_ttc) || 0,
  },
  {
    key: 'restant_du',
    label: 'Restant dû',
    render: (r) => formatMoney(r.restant_du),
    sortValue: (r) => Number(r.restant_du) || 0,
  },
  {
    key: 'solde',
    label: 'Statut',
    render: (r) =>
      r.solde ? (
        <Badge color="teal" variant="light" size="sm">
          Soldée
        </Badge>
      ) : (
        <Badge color="orange" variant="light" size="sm">
          En cours
        </Badge>
      ),
  },
];

const ENGIN_COLUMNS = [
  {
    key: 'marque_nom',
    label: 'Marque',
    render: (r) => r.marque_nom || '—',
  },
  {
    key: 'modele_nom',
    label: 'Modèle',
    render: (r) => r.modele_nom || '—',
  },
  {
    key: 'num_serie',
    label: 'N° série',
    render: (r) => r.num_serie || '—',
  },
  {
    key: 'num_parc',
    label: 'N° parc',
    render: (r) => r.num_parc || '—',
  },
  {
    key: 'annee_mec',
    label: 'Année',
    render: (r) => r.annee_mec || r.annee_creation || '—',
  },
  {
    key: 'electrique',
    label: 'Type',
    render: (r) =>
      r.electrique ? (
        <Badge color="yellow" variant="light" size="sm">
          Électrique
        </Badge>
      ) : (
        'Thermique'
      ),
  },
];

const REGLEMENT_COLUMNS = [
  {
    key: 'date_reglement',
    label: 'Date',
    render: (r) => formatDate(r.date_reglement),
    sortValue: (r) => r.date_reglement || '',
  },
  {
    key: 'mode_label',
    label: 'Mode',
    render: (r) => r.mode_label || '—',
  },
  {
    key: 'ref_reglement',
    label: 'Réf.',
    render: (r) => r.ref_reglement || '—',
  },
  {
    key: 'montant',
    label: 'Montant',
    render: (r) => formatMoney(r.montant),
    sortValue: (r) => Number(r.montant) || 0,
  },
];

function DetailAction({ onOpen }) {
  return (
    <TableActionsGroup>
      <TableActionIcon label="Détail" variant="light" color="gray" onClick={onOpen}>
        <IconEye size={16} />
      </TableActionIcon>
    </TableActionsGroup>
  );
}

export default function ClientPortalHome({ user }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [controles, setControles] = useState([]);
  const [factures, setFactures] = useState([]);
  const [engins, setEngins] = useState([]);
  const [reglements, setReglements] = useState([]);
  const [detailTarget, setDetailTarget] = useState(null);
  const [statsRefreshKey, setStatsRefreshKey] = useState(0);
  const [pendingRdvCount, setPendingRdvCount] = useState(0);
  const [pendingRdvActionable, setPendingRdvActionable] = useState(0);
  const [nextRdv, setNextRdv] = useState(null);
  const [focusRdv, setFocusRdv] = useState(null);
  const [activeTab, setActiveTab] = useState('synthese');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cRes, fRes, eRes, rRes, sRes] = await Promise.all([
        authFetch('/api/client-portal/controles'),
        authFetch('/api/client-portal/factures'),
        authFetch('/api/client-portal/engins'),
        authFetch('/api/client-portal/reglements'),
        authFetch('/api/client-portal/stats'),
      ]);
      const [cData, fData, eData, rData, sData] = await Promise.all([
        parseJsonResponse(cRes),
        parseJsonResponse(fRes),
        parseJsonResponse(eRes),
        parseJsonResponse(rRes),
        parseJsonResponse(sRes),
      ]);

      if (!cRes.ok || !fRes.ok || !eRes.ok || !rRes.ok || !sRes.ok) {
        throw new Error(
          cData.message ||
            fData.message ||
            eData.message ||
            rData.message ||
            sData.message ||
            'Chargement impossible',
        );
      }

      setControles(cData.data || []);
      setFactures(fData.data || []);
      setEngins(eData.data || []);
      setReglements(rData.data || []);
      setPendingRdvCount(sData.data?.rdv?.pending ?? 0);
      setPendingRdvActionable(sData.data?.rdv?.pending_actionable ?? 0);
      setNextRdv(sData.data?.rdv?.next ?? null);
    } catch (err) {
      setError(err.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRdvResponded = useCallback(() => {
    setStatsRefreshKey((k) => k + 1);
    setFocusRdv(null);
    loadData();
  }, [loadData]);

  const handleQuickNextRdv = useCallback(() => {
    if (!nextRdv) return;
    setFocusRdv(nextRdv);
    setActiveTab('planning');
  }, [nextRdv]);

  const handleFocusRdvHandled = useCallback(() => {
    setFocusRdv(null);
  }, []);

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader type="dots" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Title order={3}>
            Bienvenue{user.client_label ? `, ${user.client_label}` : ''}
          </Title>
          {nextRdv ? (
            <Button
              variant="light"
              color={nextRdv.client_rdv_status === 'pending' ? 'orange' : 'violet'}
              leftSection={<IconCalendarEvent size={16} />}
              onClick={handleQuickNextRdv}
            >
              Prochain RDV — {formatNextRdvLabel(nextRdv.occurred_at)}
              {nextRdv.client_rdv_status === 'pending' ? ' · à confirmer' : ''}
            </Button>
          ) : null}
        </Group>
      </Paper>

      {error ? (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Erreur">
          {error}
        </Alert>
      ) : null}

      {pendingRdvCount > 0 ? (
        <Alert
          icon={<IconCalendarEvent size={18} />}
          color="orange"
          title={
            pendingRdvCount > 1
              ? `${pendingRdvCount} rendez-vous en attente de confirmation`
              : 'Un rendez-vous en attente de confirmation'
          }
        >
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            <Text size="sm">
              {pendingRdvActionable > 0
                ? `Consultez le planning pour accepter ou refuser ${
                    pendingRdvCount > 1 ? 'ces rendez-vous' : 'ce rendez-vous'
                  }.`
                : 'Consultez le planning pour voir le détail (le créneau est peut-être déjà passé).'}
            </Text>
            <Button
              size="xs"
              variant="light"
              color="orange"
              onClick={() => setActiveTab('planning')}
            >
              Voir le planning
            </Button>
          </Group>
        </Alert>
      ) : null}

      <Tabs value={activeTab} onChange={setActiveTab} color={getAccentColor()}>
        <Tabs.List>
          <Tabs.Tab value="synthese">Synthèse</Tabs.Tab>
          <Tabs.Tab value="planning">
            <Group gap={6} wrap="nowrap">
              Planning
              {pendingRdvCount > 0 ? (
                <Badge size="xs" color="orange" variant="filled">
                  {pendingRdvCount}
                </Badge>
              ) : null}
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="controles">Contrôles ({controles.length})</Tabs.Tab>
          <Tabs.Tab value="factures">Factures ({factures.length})</Tabs.Tab>
          <Tabs.Tab value="engins">Engins ({engins.length})</Tabs.Tab>
          <Tabs.Tab value="reglements">Règlements ({reglements.length})</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="synthese" pt="md">
          <ClientPortalStatsPanel refreshKey={statsRefreshKey} />
        </Tabs.Panel>

        <Tabs.Panel value="planning" pt="md">
          <ClientPortalPlanningPanel
            refreshKey={statsRefreshKey}
            onRdvResponded={handleRdvResponded}
            focusRdv={focusRdv}
            onFocusRdvHandled={handleFocusRdvHandled}
            exploitantLabel={user?.exploitant?.raison_sociale || "l'exploitant"}
          />
        </Tabs.Panel>

        <Tabs.Panel value="controles" pt="md">
          <JlfcPaginatedTable
            records={controles}
            columns={CONTROLE_COLUMNS}
            defaultSortBy="date_controle"
            defaultSortDir="desc"
            showActifFilter={false}
            searchPlaceholder="Rechercher un contrôle (engin, facture)…"
            searchName="jlfc-client-controles-search"
            emptyText="Aucun contrôle enregistré"
            emptyFilteredText="Aucun contrôle ne correspond à votre recherche."
            tableSummary={(rows) => (rows.length > 0 ? <ControlesTableSummary rows={rows} /> : null)}
            renderActions={(row) => (
              <DetailAction onOpen={() => setDetailTarget({ type: 'controle', id: row.id })} />
            )}
          />
        </Tabs.Panel>

        <Tabs.Panel value="factures" pt="md">
          <JlfcPaginatedTable
            records={factures}
            columns={FACTURE_COLUMNS}
            defaultSortBy="date_emission"
            defaultSortDir="desc"
            showActifFilter={false}
            searchPlaceholder="Rechercher une facture (n°, montant)…"
            searchName="jlfc-client-factures-search"
            emptyText="Aucune facture"
            emptyFilteredText="Aucune facture ne correspond à votre recherche."
            tableSummary={(rows) => (rows.length > 0 ? <FacturesTableSummary rows={rows} /> : null)}
            renderActions={(row) => (
              <DetailAction onOpen={() => setDetailTarget({ type: 'facture', id: row.id })} />
            )}
          />
        </Tabs.Panel>

        <Tabs.Panel value="engins" pt="md">
          <JlfcPaginatedTable
            records={engins}
            columns={ENGIN_COLUMNS}
            defaultSortBy="num_serie"
            defaultSortDir="asc"
            showActifFilter={false}
            searchPlaceholder="Rechercher un engin (marque, modèle, n° série)…"
            searchName="jlfc-client-engins-search"
            emptyText="Aucun engin enregistré"
            emptyFilteredText="Aucun engin ne correspond à votre recherche."
            tableSummary={(rows) => (rows.length > 0 ? <EnginsTableSummary rows={rows} /> : null)}
            renderActions={(row) => (
              <DetailAction onOpen={() => setDetailTarget({ type: 'engin', id: row.id })} />
            )}
          />
        </Tabs.Panel>

        <Tabs.Panel value="reglements" pt="md">
          <JlfcPaginatedTable
            records={reglements}
            columns={REGLEMENT_COLUMNS}
            defaultSortBy="date_reglement"
            defaultSortDir="desc"
            showActifFilter={false}
            searchPlaceholder="Rechercher un règlement (mode, réf.)…"
            searchName="jlfc-client-reglements-search"
            emptyText="Aucun règlement enregistré"
            emptyFilteredText="Aucun règlement ne correspond à votre recherche."
            tableSummary={(rows) => (rows.length > 0 ? <ReglementsTableSummary rows={rows} /> : null)}
            renderActions={(row) => (
              <DetailAction onOpen={() => setDetailTarget({ type: 'reglement', id: row.id })} />
            )}
          />
        </Tabs.Panel>
      </Tabs>

      <ClientPortalDetailModal
        opened={Boolean(detailTarget)}
        onClose={() => setDetailTarget(null)}
        type={detailTarget?.type}
        recordId={detailTarget?.id}
      />
    </Stack>
  );
}
